
import React, { useRef, useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import VoiceOutput from "./VoiceOutput";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatMessageListProps {
  messages: Message[];
  user: any;
  onMessageClick: (content: string) => void;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  user,
  onMessageClick,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const formatMessageContent = (content: string) => {
    // Updated regex pattern to match markdown-style links [text](/receta/id)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    // Replace markdown links with properly clickable spans
    const formattedContent = content.replace(
      markdownLinkRegex,
      (match, text, url) => {
        // Ensure the URL is properly formatted
        if (url.startsWith('/receta/')) {
          return `<span class="recipe-link cursor-pointer text-vegana-primary font-medium hover:underline" data-url="${url}">${text}</span>`;
        }
        return match; // Keep the original format if not a recipe link
      }
    );
    
    // Create a new div element to render the HTML content
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: formattedContent }} 
        onClick={(e) => {
          // Handle click on recipe link
          const target = e.target as HTMLElement;
          if (target.classList.contains('recipe-link')) {
            const url = target.getAttribute('data-url');
            if (url) {
              e.stopPropagation();
              onMessageClick(url);
            }
          }
        }}
      />
    );
  };

  return (
    <>
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}
        >
          {!message.isUser && (
            <div className="flex-shrink-0 mr-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                <AvatarFallback>L</AvatarFallback>
              </Avatar>
            </div>
          )}
          <div
            className={`max-w-[80%] p-3 rounded-lg ${
              message.isUser
                ? "bg-vegana-primary text-white rounded-tr-none text-right"
                : "bg-white border border-gray-200 shadow-sm rounded-tl-none text-left"
            }`}
          >
            {message.isUser ? (
              <p className="text-sm">{message.content}</p>
            ) : (
              <div className="text-sm space-y-2">
                {formatMessageContent(message.content)}
                <div className="flex justify-end mt-1">
                  <VoiceOutput text={message.content} />
                </div>
              </div>
            )}
            <p
              className={`text-xs mt-1 ${
                message.isUser ? "text-white/70" : "text-gray-500"
              }`}
            >
              {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          {message.isUser && (
            <div className="flex-shrink-0 ml-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </>
  );
};

export default ChatMessageList;
